/* eslint-disable  @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
import cloneDeep from 'lodash/cloneDeep';
import { MS_IN_DAY, MS_IN_HOUR, MS_IN_MINUTE } from 'utils/time-utils';
import { EditorScene } from '../../editor/EditorScene';
import { browseToAndLoadFile, saveFile } from '../../utils/FileIO';
import { type LevelData } from '../../game/level/LevelData';
import { DebugSectionId } from './DebugSectionId';
import { type DebugSectionConfig } from './DebugPlugin';
import { EMPTY_LEVEL } from '../../game/level/EmptyLevel';
import type { App } from '../App';
import { getDebugGameWindow } from './getDebugGameWindow';
import type { Scene } from '../SceneManager';
import { MainMenuScene } from '../../mainMenu/MainMenuScene';
import { activeSplitTests } from '../splitTest/activeSplitTests';
import type { DebugFields } from '../devTools/DevToolsPlugin';
import { BoosterType } from '../../game/boosters/types';
import { FeatureCooldownType } from '../state/cooldowns/types';
import SoftgamesSplitTestService from '../splitTest/SoftgamesSplitTestService';
import { GameSceneMVC } from '../../game/scene/GameSceneMVC';
import type { BoardRecording } from '../../game/boardMVC/BoardRecorder';
import { BoardRecorder } from '../../game/boardMVC/BoardRecorder';
import { BoardPlaybackError, testRecording } from '../../game/boardMVC/BoardRecordingPlayback';
import { TileStackView } from '../../game/boardMVC/views/board/TileStackView';
import { FakeTournaments } from '../platform/web/FakeTournaments';
import { FakeLeaderboards } from '../platform/web/FakeLeaderboards';
import { createActionDialog } from '../../libs/HTML/HTMLElementCreator';
import { showRefillsDebugPanel } from './showRefillsDebugPanel';
import { debugCreateAlmostUnlockedState, debugCreateFullyUnlockedState } from '../state/diorama/DioramaStateUtils';
import { BoardRecordingVisualPlayback } from './utils/BoardRecordingVisualPlayback';
import { showLevelTimer } from './showLevelTimer';
import { openOfferEditor } from '../offers/editor/openOfferEditor';
import { initFileDropHandler } from './levelFileDropHandler';
import { generateAssetsLayoutReport } from './generateAssetsLayoutReport';
import { clearLockedSaveState, saveCurrentSaveStateAsLocked, pickLockedSaveState } from './utils/debugLockState';
import { getSolverDebugMenuFunctions } from '../../game/solver/getSolverDebugMenuFunctions';
import { addFPSCounter } from './addFPSCounter';
import { testShadowDrop } from './testShadowDrop';
import { getLevelDataHash, RAW_LEVELS } from '../../../levels';
import { getHash } from '../utils/getHash';
import { showDebugTimeWidget } from './utils/showDebugTimeWidget';

const originalDateNow = Date.now;
let dateOffset = 0;

function unwrapCallbackRecordOfRecords(
  recordOfRecords: Record<string, Record<string, () => void>>,
): DebugSectionConfig[] {
  return Object.entries(recordOfRecords).map(([key, callbacks]) => ({
    id: key,
    title: key,
    expanded: false,
    fields: unwrapCallbackRecord(callbacks),
  }));
}

function unwrapCallbackRecord(callbacks: Record<string, () => void>): DebugFields {
  const out: DebugFields = {};
  Object.entries(callbacks).forEach(([key, cb]) => {
    out[key] = {
      type: 'button',
      title: key,
      cb,
    };
  });
  return out;
}

export function getDebugSectionConfig(app: App): DebugSectionConfig[] {
  const { stateController, gameEvents } = app;

  Date.now = (): number => originalDateNow() + dateOffset;

  const showScene = (scene: Scene): void => {
    app.controller.popups.view.autoOverlayFading = true;
    app.controller.popups.closeAllPopups();
    app.controller.sceneManager.activateScene(scene);
  };

  const launchMainMenuScene = (): void => {
    showScene(new MainMenuScene(app));
  };

  const launchGameScene = (levelData: LevelData, isTestLevel = false): GameSceneMVC => {
    const scene = new GameSceneMVC({
      app,
      levelData: cloneDeep(levelData),
      saveProgress: !isTestLevel,
    });
    showScene(scene);
    return scene;
  };

  const launchEditorScene = (levelData?: LevelData): EditorScene => {
    const scene = new EditorScene({ app, levelData });
    showScene(scene);
    return scene;
  };
  // setTimeout(launchEditorScene, 0);

  const showNextLevel = (): void => {
    stateController.setNextLevel();
    launchGameScene(stateController.currentLevelData);
  };

  const showPrevLevel = (): void => {
    stateController.setPreviousLevel();
    launchGameScene(stateController.currentLevelData);
  };

  const resetState = (): void => {
    createActionDialog({
      onConfirm: () => {
        getDebugGameWindow(app).resetState();
      },
      titleText: 'Are you sure you would like to reset your game state?',
      zIndex: Number.MAX_SAFE_INTEGER,
    });
  };

  const resetTournamentFeature = (): void => {
    app.stateController.setSavedTournaments([]);
    FakeTournaments.debugClearTournaments();
    FakeLeaderboards.debugClearLeaderboards();
  };

  initFileDropHandler((levelData) => {
    const { activeScene } = app.controller.sceneManager;

    if (activeScene instanceof GameSceneMVC) {
      launchGameScene(levelData, true);
    } else {
      launchEditorScene(levelData);
    }
  });

  return [
    {
      id: DebugSectionId.Navigation,
      title: 'Navigation',
      expanded: true,
      fields: {
        goToGameScene: {
          type: 'button',
          title: 'Game Scene',
          cb: (): void => {
            const { debug } = app;
            const { activeScene } = app.controller.sceneManager;

            if (!debug) return;
            if (activeScene instanceof GameSceneMVC) return;

            launchGameScene(stateController.currentLevelData);
          },
        },
        goToMainMenu: {
          type: 'button',
          title: 'Main Menu',
          cb: (): void => {
            const { debug } = app;
            const { activeScene } = app.controller.sceneManager;

            if (!debug) return;
            if (activeScene instanceof MainMenuScene) return;

            launchMainMenuScene();
          },
        },
        goToEditorScene: {
          type: 'button',
          title: 'Editor Scene',
          cb: (): void => {
            const { debug } = app;
            const { activeScene } = app.controller.sceneManager;

            if (!debug) return;
            if (activeScene instanceof EditorScene) return;

            launchEditorScene();
          },
        },
      },
    },
    {
      id: DebugSectionId.State,
      title: 'State',
      expanded: false,
      showOnSceneActivate: () => true,
      fields: {
        resetState: {
          type: 'button',
          title: 'Reset State',
          cb: resetState,
        },
        resetAskFriend: {
          type: 'button',
          title: 'reset ask friend',
          cb: (): void => {
            stateController.setFeatureCooldownTime(FeatureCooldownType.LifeRequest, Date.now());
          },
        },
        resetTournaments: {
          type: 'button',
          title: 'Reset tournaments (WEB)',
          cb: resetTournamentFeature,
        },
        unlockAllDioramas: {
          type: 'button',
          title: 'Unlock all dioramas',
          cb: (): void => {
            stateController.debugSetData({
              diorama: debugCreateFullyUnlockedState(),
            });
          },
        },
        almostUnlockDioramas: {
          type: 'button',
          title: 'Almost unlock first diorama',
          cb: (): void => {
            stateController.debugSetData({
              diorama: debugCreateAlmostUnlockedState(),
            });
          },
        },
        lockCurrentState: {
          type: 'button',
          title: 'Save current state',
          cb: (): void => {
            saveCurrentSaveStateAsLocked();
          },
        },
        pickSaveState: {
          type: 'button',
          title: 'Pick locked state',
          cb: (): void => {
            pickLockedSaveState();
          },
        },
        clearStateLock: {
          type: 'button',
          title: 'Clear locked state',
          cb: (): void => {
            clearLockedSaveState();
          },
        },
      },
    },
    {
      id: DebugSectionId.Tournaments,
      title: 'Tournaments (WEB)',
      expanded: false,
      showOnSceneActivate: () => true,
      fields: {
        resetTournaments: {
          type: 'button',
          title: 'Reset tournaments (WEB)',
          cb: resetTournamentFeature,
        },
        openThroughTournament: {
          type: 'button',
          title: 'Entry by friends tournament',
          cb: (): void => {
            FakeTournaments.setEntryTournamentForNextSession();
            window.location.reload();
          },
        },
      },
    },
    {
      id: DebugSectionId.Game,
      title: 'Game Scene',
      expanded: true,
      showOnSceneActivate: (scene: Scene): boolean => scene instanceof GameSceneMVC,
      fields: {
        openInEditor: {
          type: 'button',
          title: 'Open in Editor',
          cb: (): void => {
            const { activeScene } = app.controller.sceneManager;
            if (activeScene instanceof GameSceneMVC) {
              const levelData = activeScene.exportOriginalLevelData();
              launchEditorScene().restoreState(levelData);
            }
          },
        },
        exportOriginalLevelData: {
          type: 'button',
          title: 'Save Level',
          cb: (): void => {
            const { activeScene } = app.controller.sceneManager;
            if (activeScene instanceof GameSceneMVC) {
              saveFile(JSON.stringify(activeScene.exportOriginalLevelData(), null, 2), 'level.json');
            }
          },
        },
        exportLevelData: {
          type: 'button',
          title: 'Save Level State',
          cb: (): void => {
            const { activeScene } = app.controller.sceneManager;
            if (activeScene instanceof GameSceneMVC) {
              saveFile(JSON.stringify(activeScene.exportLevelData(), null, 2), 'level.json');
            }
          },
        },
        loadState: {
          type: 'button',
          title: 'Load Level',
          cb: (): void => {
            browseToAndLoadFile('.json', (data: string) => {
              const levelData: LevelData = { ...EMPTY_LEVEL, ...(JSON.parse(data) as LevelData) };
              const { activeScene } = app.controller.sceneManager;

              if (activeScene instanceof GameSceneMVC) {
                launchGameScene(levelData, true);
              }
            });
          },
        },
        pasteLevel: {
          type: 'button',
          title: 'Pase Level',
          cb: (): void => {
            // eslint-disable-next-line no-alert
            const pastedLevelData = JSON.parse(prompt('paste level data') ?? '') as LevelData;

            const levelData: LevelData = { ...EMPTY_LEVEL, ...pastedLevelData };
            const { activeScene } = app.controller.sceneManager;

            if (activeScene instanceof GameSceneMVC) {
              launchGameScene(levelData, true);
            }
          },
        },
        nextLevel: {
          type: 'button',
          title: ' -> Next Level',
          cb: showNextLevel,
        },
        next10Level: {
          type: 'button',
          title: ' -> 10 Levels',
          cb: (): void => {
            for (let i = 0; i < 10; i += 1) {
              stateController.setNextLevel();
            }
            launchGameScene(stateController.currentLevelData);
          },
        },
        prevLevel: {
          type: 'button',
          title: '<- Prev Level',
          cb: showPrevLevel,
        },
        prev10Level: {
          type: 'button',
          title: '<- 10 Levels',
          cb: (): void => {
            for (let i = 0; i < 10; i += 1) {
              stateController.setPreviousLevel();
            }
            launchGameScene(stateController.currentLevelData);
          },
        },
        triggerWin: {
          type: 'button',
          title: 'Win level',
          cb: (): void => {
            const { activeScene } = app.controller.sceneManager;
            if (activeScene instanceof GameSceneMVC) {
              app.controller.popups.closeAllPopups();
              activeScene.debugShowLevelComplete();
            }
          },
        },
        triggerFail: {
          type: 'button',
          title: 'Lose level',
          cb: (): void => {
            const { activeScene } = app.controller.sceneManager;
            if (activeScene instanceof GameSceneMVC) {
              app.controller.popups.closeAllPopups();
              activeScene.debugShowLevelFail();
            }
          },
        },
      },
    },
    {
      id: DebugSectionId.Tools,
      title: 'Tools',
      expanded: false,
      fields: {
        layoutAnimation: {
          type: 'button',
          title: 'Open layout animation menu',
          cb: (): void => {
            app.debugUtils.layoutGallery.open();
          },
        },
        offerEditor: {
          type: 'button',
          title: 'Open offer editor',
          cb: openOfferEditor,
        },
        layoutAssetReport: {
          type: 'button',
          title: 'Layouts asset report',
          cb: generateAssetsLayoutReport,
        },
        logLevelHashes: {
          type: 'button',
          title: 'Log level hashes',
          cb: (): void => {
            console.log({
              all: getHash(RAW_LEVELS),
              levels: RAW_LEVELS.map((level) => getLevelDataHash(level)),
            });
          },
        },
        fpsCounter: {
          type: 'button',
          title: 'Add FPS counter',
          cb: addFPSCounter,
        },
        testDropShadow: {
          type: 'button',
          title: 'Drop shadow',
          cb: testShadowDrop,
        },
      },
    },
    {
      id: DebugSectionId.Board,
      title: 'Board recording',
      expanded: false,
      showOnSceneActivate: (scene: Scene): boolean => scene instanceof GameSceneMVC,
      fields: ((): DebugFields => {
        let recorder: BoardRecorder | null = null;

        return {
          startRecording: {
            type: 'button',
            title: 'Start recording board',
            cb: (): void => {
              const scene = app.controller.sceneManager.activeScene;
              if (!(scene instanceof GameSceneMVC)) return;

              recorder = new BoardRecorder(scene.debugGetBoardModel());
              recorder.startRecording();
            },
          },
          stopRecording: {
            type: 'button',
            title: 'Stop recording',
            cb: (): void => {
              if (!recorder) return;
              const recording = recorder.stop();
              if (!recording) return;
              recorder = null;
              saveFile(JSON.stringify(recording, null, '  '), `board_recording_${Date.now()}.json`);
            },
          },
          playbackRecording: {
            type: 'button',
            title: 'Playback recording',
            cb: (): void => {
              browseToAndLoadFile('.json', async (data: string): Promise<void> => {
                const parsedData = JSON.parse(data) as BoardRecording;

                if (parsedData.initState && parsedData.moves) {
                  try {
                    await testRecording(parsedData);
                  } catch (e) {
                    if (e instanceof BoardPlaybackError) {
                      console.log(e, e.details);
                    }
                    console.log(e);
                  }
                }
              });
            },
          },
          playbackSceneRecording: {
            type: 'button',
            title: 'Visual playback',
            cb: (): void => {
              browseToAndLoadFile('.json', async (data: string): Promise<void> => {
                const parsedData = JSON.parse(data) as BoardRecording;
                void new BoardRecordingVisualPlayback(parsedData);
              });
            },
          },
          debugStackView: {
            type: 'button',
            title: 'Toggle debug stack view',
            cb: (): void => {
              TileStackView.addDebugView = !TileStackView.addDebugView;
            },
          },
        };
      })(),
    },
    {
      id: DebugSectionId.Solver,
      title: 'Solver',
      expanded: false,
      fields: unwrapCallbackRecord(getSolverDebugMenuFunctions(app)),
    },
    {
      id: DebugSectionId.MainMenu,
      title: 'Main Menu',
      expanded: true,
      showOnSceneActivate: (scene: Scene): boolean => scene instanceof MainMenuScene,
      fields: {
        nextLevel: {
          type: 'button',
          title: ' -> Next Level',
          cb: showNextLevel,
        },
        prevLevel: {
          type: 'button',
          title: '<- Prev Level',
          cb: showPrevLevel,
        },
      },
    },
    {
      id: DebugSectionId.Editor,
      title: 'Editor Scene',
      expanded: true,
      showOnSceneActivate: (scene: Scene): boolean => scene instanceof EditorScene,
      fields: {
        exportState: {
          type: 'button',
          title: 'Save Level',
          cb: (): void => {
            const { activeScene } = app.controller.sceneManager;
            if (activeScene instanceof EditorScene) {
              saveFile(JSON.stringify(activeScene.exportLevelData(), null, 2), 'level.json');
            }
          },
        },
        loadState: {
          type: 'button',
          title: 'Load Level',
          cb: (): void => {
            browseToAndLoadFile('.json', (data: string) => {
              const { activeScene } = app.controller.sceneManager;
              const levelData: LevelData = { ...EMPTY_LEVEL, ...(JSON.parse(data) as LevelData) };
              if (activeScene instanceof EditorScene) {
                activeScene.restoreState(levelData);
              }
            });
          },
        },
        testLevel: {
          type: 'button',
          title: 'Test Level',
          cb: (): void => {
            const { activeScene } = app.controller.sceneManager;
            if (activeScene instanceof EditorScene) {
              const levelData = activeScene.exportLevelData();
              const gameScene = launchGameScene(levelData, true);
              showRefillsDebugPanel(gameScene, app.controller.sceneManager);
              showLevelTimer(app.controller.sceneManager);
            }
          },
        },
      },
    },
    {
      id: DebugSectionId.SplitTest,
      title: 'Split Tests',
      expanded: false,
      fields: ((): DebugFields => {
        const fields: DebugFields = {};

        activeSplitTests.forEach((splitTest) => {
          for (let distribution = 1; distribution <= splitTest.distributions; distribution += 1) {
            const testName = splitTest.newUserTestSlug ?? splitTest.returningUserTestSlug ?? 'Test';
            const fieldName = `${testName}_${distribution}`;
            fields[fieldName] = {
              title: fieldName,
              type: 'button',
              // eslint-disable-next-line @typescript-eslint/no-loop-func
              cb: (): void => {
                SoftgamesSplitTestService.setDebugOverrideForTest(splitTest, distribution);
                window.location.reload();
              },
            };
          }
        });

        fields.clear = {
          title: 'Clear debug overrides',
          type: 'button',
          cb: (): void => {
            SoftgamesSplitTestService.clearDebugOverride();
          },
        };
        return fields;
      })(),
    },
    ...unwrapCallbackRecordOfRecords(gameEvents.getDebugFunctions()),
    {
      id: DebugSectionId.Resources,
      title: 'Resources',
      expanded: true,
      fields: {
        addLife: {
          type: 'button',
          title: '+ 1 life',
          cb: (): void => {
            stateController.setLives(stateController.getLives() + 1);
          },
        },
        removeLife: {
          type: 'button',
          title: '- 1 life',
          cb: (): void => {
            stateController.setLives(stateController.getLives() - 1);
          },
        },
        addUnlimitedLivesTime: {
          type: 'button',
          title: '+ 1 min unlimited lives',
          cb: (): void => {
            stateController.setUnlimitedLivesMinutes(stateController.getUnlimitedLivesMinutes() + 1);
          },
        },
        removeUnlimitedLivesTime: {
          type: 'button',
          title: '- 30 min unlimited lives',
          cb: (): void => {
            stateController.setUnlimitedLivesMinutes(stateController.getUnlimitedLivesMinutes() - 30);
          },
        },
        addCoins: {
          type: 'button',
          title: '+ 5000 coins',
          cb: (): void => {
            stateController.setCoins(stateController.getCoins() + 5000);
          },
        },
        removeCoins: {
          type: 'button',
          title: '- 5000 coins',
          cb: (): void => {
            stateController.setCoins(Math.max(stateController.getCoins() - 5000, 0));
          },
        },
        addHammerBooster: {
          type: 'button',
          title: '+ Hammer booster',
          cb: (): void => {
            stateController.setBoosterInventoryAmount(
              BoosterType.Hammer,
              stateController.getBoosterInventoryAmount(BoosterType.Hammer) + 1,
            );
          },
        },
        addMoveBooster: {
          type: 'button',
          title: '+ Move booster',
          cb: (): void => {
            stateController.setBoosterInventoryAmount(
              BoosterType.Move,
              stateController.getBoosterInventoryAmount(BoosterType.Move) + 1,
            );
          },
        },
        addRedrawBooster: {
          type: 'button',
          title: '+ Redraw booster',
          cb: (): void => {
            stateController.setBoosterInventoryAmount(
              BoosterType.Redraw,
              stateController.getBoosterInventoryAmount(BoosterType.Redraw) + 1,
            );
          },
        },
        addUndoBooster: {
          type: 'button',
          title: '+ Undo booster',
          cb: (): void => {
            stateController.setBoosterInventoryAmount(
              BoosterType.Undo,
              stateController.getBoosterInventoryAmount(BoosterType.Undo) + 1,
            );
          },
        },
        removeAllBoosters: {
          type: 'button',
          title: '- All boosters',
          cb: (): void => {
            Object.values(BoosterType).forEach((booster) => {
              stateController.setBoosterInventoryAmount(booster, 0);
            });
          },
        },
      },
    },
    {
      id: DebugSectionId.Time,
      title: 'Time',
      expanded: false,
      fields: {
        timeWidget: {
          type: 'button',
          title: 'Show time widget',
          cb: showDebugTimeWidget,
        },
        timeAddEightHour: {
          type: 'button',
          title: 'Time + 8 hour',
          cb: (): void => {
            dateOffset += 8 * MS_IN_HOUR;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
        timeSubtractEightHour: {
          type: 'button',
          title: 'Time - 8 hour',
          cb: (): void => {
            dateOffset -= 8 * MS_IN_HOUR;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
        timeAddOneHour: {
          type: 'button',
          title: 'Time + 1 hour',
          cb: (): void => {
            dateOffset += 1 * MS_IN_HOUR;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
        timeSubtractOneHour: {
          type: 'button',
          title: 'Time - 1 hour',
          cb: (): void => {
            dateOffset -= 1 * MS_IN_HOUR;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
        timeAddFifteenMinute: {
          type: 'button',
          title: 'Time + 15 minutes',
          cb: (): void => {
            dateOffset += 15 * MS_IN_MINUTE;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
        timeSubtractFifteenMinute: {
          type: 'button',
          title: 'Time - 15 minutes',
          cb: (): void => {
            dateOffset -= 15 * MS_IN_MINUTE;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
        timeAddOneMinute: {
          type: 'button',
          title: 'Time + 1 minute',
          cb: (): void => {
            dateOffset += 1 * MS_IN_MINUTE;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
        timeSubtractOneMinute: {
          type: 'button',
          title: 'Time - 1 minute',
          cb: (): void => {
            dateOffset -= 1 * MS_IN_MINUTE;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
        timeReset: {
          type: 'button',
          title: 'Time reset',
          cb: (): void => {
            dateOffset = 0;
            app.debug?.updateSectionTitle(DebugSectionId.Time, `Time ${formatTime(dateOffset)}`);
          },
        },
      },
    },
    ...(app.platform.cheatOptions?.cheatOptions() ?? []),
  ];
}

function formatTime(time: number): string {
  if (time === 0) return '';

  const sign = time >= 0 ? '+' : '-';
  const absTime = Math.abs(time);

  const days = Math.floor(absTime / MS_IN_DAY);
  const hours = Math.floor((absTime % MS_IN_DAY) / MS_IN_HOUR);
  const minutes = Math.floor((absTime % MS_IN_HOUR) / MS_IN_MINUTE);

  const result = [];
  if (days > 0) result.push(`${days}d`);
  if (hours > 0) result.push(`${hours}h`);
  if (minutes > 0) result.push(`${minutes}m`);

  return `${sign}${result.join(' ')}`;
}
